import { ReactNode } from "react"
import { Box, Link, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons"
import { makeStyles } from "tss-react/mui"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../../shared/components/links/StandardBreadcrumbs"
import {
    SegmentedControlOption,
    StandardSegmentedControl,
} from "../../../../../../../shared/components/segmentedControl/StandardSegmentedControl"
import { MERCHANT_REPORTS_ROUTE } from "../../../../../../../constants/routes"

import { FilteredStatuses } from "../../meetingServiceTypes"
import { ProductionGroupBy } from "../helpers"
import { NotificationTopBarButton } from "../../../../../../../shared/layouts/components/NotificationTopBar/NotificationTopBarButton"
import { ProductionStatusSummary } from "./ProductionStatusSummary"

const useStyles = makeStyles()((theme) => ({
    fullscreenLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".375rem",
        cursor: "pointer",
        whiteSpace: "nowrap",
        color: theme.palette.primary.main,
    },
    fullscreenIcon: {
        fontSize: "1rem",
    },
    fullscreenText: {
        fontWeight: 700,
    },
    tabsRow: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: ".75rem",
        paddingBottom: theme.spacing(3),
        [theme.breakpoints.up("lg")]: {
            flexDirection: "row",
            alignItems: "center",
            gap: "1.5rem",
        },
    },
    titleTabsGroup: {
        flexWrap: "wrap",
        rowGap: ".75rem",
        width: "100%",
        [theme.breakpoints.up("lg")]: {
            width: "auto",
            flexGrow: 1,
            minWidth: 0,
        },
    },
    tabsRowFullscreen: {
        position: "sticky",
        top: 0,
        zIndex: 3,
        padding: "1rem 1.5rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.background.paper,
        // When the bar wraps into several rows its height is unpredictable, so the hour headers
        // would slide underneath it; let it scroll away and stick the hour headers at the top instead.
        [theme.breakpoints.down("lg")]: {
            position: "static",
        },
    },
    childrenFullscreen: {
        padding: "1.5rem 1.5rem 0",
    },
    viewTitle: {
        flexShrink: 0,
        color: theme.palette.tailwind.slate[600],
    },
    segmentedControl: {
        flexShrink: 0,
    },
    notificationsButton: {
        padding: "1rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[800],
        "& svg": {
            fontSize: "1.25rem",
        },
    },
    notificationsCounter: {
        background: theme.palette.tailwind.red[600],
    },
    tabsRight: {
        width: "100%",
        flexWrap: "wrap",
        justifyContent: "flex-end",
        [theme.breakpoints.up("lg")]: {
            width: "auto",
        },
    },
    // On the stacked narrow layout the pill fills its whole row, like the design's stretched status pill.
    statusSummary: {
        [theme.breakpoints.down("md")]: {
            flexGrow: 1,
        },
    },
}))

type Props = Readonly<{
    children: ReactNode
    groupBy: ProductionGroupBy
    onGroupByChange: (value: ProductionGroupBy) => void
    isFullscreen: boolean
    notApprovedView: boolean
    onNotApprovedChange: (notApprovedView: boolean) => void
    onEnterFullscreen: () => void
    summaryStatus: FilteredStatuses
    onSummaryStatusChange: (status: FilteredStatuses) => void
    orderStats: Readonly<{ total: number; count: number }>
}>

export function ProductionViewHeader({
    children,
    groupBy,
    onGroupByChange,
    isFullscreen,
    notApprovedView,
    onNotApprovedChange,
    onEnterFullscreen,
    summaryStatus,
    onSummaryStatusChange,
    orderStats,
}: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()

    const groupingOptions: ReadonlyArray<SegmentedControlOption<ProductionGroupBy>> = [
        {
            value: ProductionGroupBy.TIME,
            label: l10n.getString("meeting-service-production-view-tab-delivery-time"),
        },
        {
            value: ProductionGroupBy.LOCATION,
            label: l10n.getString("meeting-service-production-view-tab-delivery-location"),
        },
    ]

    const handleGroupByChange = (value: ProductionGroupBy) => {
        onNotApprovedChange(false)
        onGroupByChange(value)
    }

    const statusSummary = (
        <ProductionStatusSummary
            status={summaryStatus}
            onStatusChange={onSummaryStatusChange}
            ready={orderStats.count}
            total={orderStats.total}
            className={classes.statusSummary}
        />
    )

    const titleAndTabs = (
        <>
            <LocalizedStrict id="production-meeting-service-breadcrumb">
                <Typography variant="h5" className={classes.viewTitle}>
                    Meeting service
                </Typography>
            </LocalizedStrict>
            <StandardSegmentedControl
                className={classes.segmentedControl}
                value={notApprovedView ? undefined : groupBy}
                options={groupingOptions}
                onChange={handleGroupByChange}
            />
        </>
    )

    return (
        <>
            {!isFullscreen && (
                <LocalizedStrict id="production-meeting-service-page-header" attrs={{ title: true }}>
                    <PageHeader page={Page.PRODUCTION_MEETING_SERVICE} title="Meeting service" hideTitleRow>
                        <Stack direction="row" alignItems="center" spacing="1rem">
                            <StandardBreadcrumbs>
                                <LocalizedStrict id="merchant-nav-item-home">
                                    <BreadcrumbLink to={MERCHANT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                                </LocalizedStrict>
                                <LocalizedStrict id="merchant-production">
                                    <Breadcrumb>Production</Breadcrumb>
                                </LocalizedStrict>
                                <LocalizedStrict id="production-meeting-service-breadcrumb">
                                    <Breadcrumb>Meeting service</Breadcrumb>
                                </LocalizedStrict>
                            </StandardBreadcrumbs>
                            <Link className={classes.fullscreenLink} underline="hover" onClick={onEnterFullscreen}>
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={classes.fullscreenIcon} />
                                <LocalizedStrict id="meeting-service-production-view-fullscreen-link">
                                    <Typography variant="body2" component="span" className={classes.fullscreenText}>
                                        Fullscreen Mode
                                    </Typography>
                                </LocalizedStrict>
                            </Link>
                        </Stack>
                    </PageHeader>
                </LocalizedStrict>
            )}

            <Stack className={cx(classes.tabsRow, isFullscreen && classes.tabsRowFullscreen)}>
                <Stack direction="row" alignItems="center" spacing="1.5rem" className={classes.titleTabsGroup}>
                    {titleAndTabs}
                </Stack>
                <Stack direction="row" alignItems="center" spacing="1rem" className={classes.tabsRight}>
                    {statusSummary}
                    {isFullscreen && (
                        <NotificationTopBarButton
                            buttonClassName={classes.notificationsButton}
                            counterClassName={classes.notificationsCounter}
                        />
                    )}
                </Stack>
            </Stack>

            <Box className={cx(isFullscreen && classes.childrenFullscreen)}>{children}</Box>
        </>
    )
}
